//import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { obtenerMensajeError } from "utils/apiErrors";
import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Card, IconButton, Grid, Tooltip, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";

// Componentes SUI
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiBadge from "components/SuiBadge";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import MontoInput from "./components/montoInput";
import ProyeccionModal from "./components/proyeccion/index";
import DescripcionGeneral from "./components/descripcionGeneral";

// API
import ProcesosApi from "api/procesosApi";

// Styles
import { useStyles, GridContainer, gridConfig } from "./styles";

const { HEADER_HEIGHT, ROW_HEIGHT, TOTAL_HEIGHT } = gridConfig;

const createEmptyRow = () => ({
  id: `new-${Date.now()}-${Math.random()}`,
  concepto: "",
  monto: "",
  saved: false,
});

const FinanzasForm = forwardRef(
  ({ procesoId, setFormData, setError, setDisableNext, onComplete }, ref) => {
    const classes = useStyles();
    const [ingresos, setIngresos] = useState([createEmptyRow()]);
    const [egresos, setEgresos] = useState([createEmptyRow()]);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [gridKey, setGridKey] = useState(0);
    const [showProyeccion, setShowProyeccion] = useState(false);
    const propuestaIdRef = useRef(null);
    // eslint-disable-next-line no-unused-vars
    const [propuestaId, setPropuestaId] = useState(null);
    const [hasPropuesta, setHasPropuesta] = useState(false);

    // Exponer saveFinanzas al componente padre
    useImperativeHandle(ref, () => ({
      saveFinanzas,
    }));

    FinanzasForm.displayName = "FinanzasForm";

    const [datosCargados, setDatosCargados] = useState(false);
    const totals = useMemo(() => {
      const totalIngresos = ingresos.reduce((sum, item) => sum + (parseFloat(item.monto) || 0), 0);
      const totalEgresos = egresos.reduce((sum, item) => sum + (parseFloat(item.monto) || 0), 0);
      return {
        totalIngresos,
        totalEgresos,
        disponible: totalIngresos - totalEgresos,
      };
    }, [ingresos, egresos]);
    useImperativeHandle(ref, () => ({
      saveFinanzas,
    }));

    // Cargar datos base
    useEffect(() => {
      const cargar = async () => {
        if (!procesoId || datosCargados) return;
        try {
          const finanzasResponse = await ProcesosApi.obtenerFinanzas(procesoId);
          const data = finanzasResponse.data || {};
          const ingresosData =
            data.finanzas
              ?.filter((f) => f.tipo_finanza === "INGRESO")
              .map((f) => ({
                id: f.id,
                concepto: f.tipo_ingreso,
                monto: f.valor,
                saved: true,
              })) || [];
          setIngresos(ingresosData.length > 0 ? ingresosData : [createEmptyRow()]);
          const egresosData =
            data.finanzas
              ?.filter((f) => f.tipo_finanza === "GASTO")
              .map((f) => ({
                id: f.id,
                concepto: f.tipo_gasto,
                monto: f.valor,
                saved: true,
              })) || [];
          setEgresos(egresosData.length > 0 ? egresosData : [createEmptyRow()]);
          const procesoResponse = await ProcesosApi.fetchProcesoById(procesoId);
          if (procesoResponse.data) {
            setFormData((prevData) => ({
              ...prevData,
              compromiso_mensual: procesoResponse.data.compromiso_mensual || "0",
            }));
          }
          setDatosCargados(true);
        } catch (error) {
          console.error("Error al cargar datos:", error);
          setError(obtenerMensajeError(error, "Error al cargar los datos financieros"));
        }
      };
      cargar();
    }, [procesoId, datosCargados]);

    // Verificar propuesta (solo una vez)
    useEffect(() => {
      const verificar = async () => {
        if (!procesoId || propuestaIdRef.current) return;
        console.log("Verificando propuesta...");
        try {
          const propuestasResponse = await ProcesosApi.obtenerPropuestas(procesoId, "solicitud");
          const propuestaActiva = propuestasResponse.data[0];
          setHasPropuesta(!!propuestaActiva);
          setDisableNext(!propuestaActiva);
          if (propuestaActiva?.id) {
            propuestaIdRef.current = propuestaActiva.id;
            setPropuestaId(propuestaActiva.id);
          } else {
            setDisableNext(true);
          }
        } catch (error) {
          console.error("Error verificando propuesta:", error);
          setDisableNext(true);
        }
      };

      verificar();
    }, [procesoId, propuestaIdRef]);

    useEffect(() => {
      const totalIngresos = ingresos.reduce((sum, item) => {
        const monto = parseFloat(item.monto) || 0;
        return sum + monto;
      }, 0);
      const totalEgresos = egresos.reduce((sum, item) => {
        const monto = parseFloat(item.monto) || 0;
        return sum + monto;
      }, 0);
      const disponible = totalIngresos - totalEgresos;
      setFormData((prevData) => ({
        ...prevData,
        compromiso_mensual: disponible.toString(),
      }));
    }, [totals, setFormData]);

    const handleProyeccionComplete = async (proyeccionData) => {
      try {
        if (proyeccionData?.success && proyeccionData?.unblockNext) {
          console.log("Desbloqueando botón siguiente...");
          setDisableNext(false);
        }
        // Actualizar estados locales
        setHasPropuesta(true);
        if (proyeccionData.propuestaId && proyeccionData.propuestaId !== propuestaIdRef.current) {
          propuestaIdRef.current = proyeccionData.propuestaId;
          setPropuestaId(proyeccionData.propuestaId);
        }
        // Actualizar formulario
        setFormData((prev) => ({
          ...prev,
          compromiso_mensual: proyeccionData.cuotaMensual?.toString() || prev.compromiso_mensual,
        }));
        // Llamar al onComplete con los datos correctos
        if (onComplete) {
          // Notifica al padre que se ha completado
          onComplete({ unblockNext: true, success: true, propuestaId: proyeccionData.propuestaId });
        }
        console.log("handleProyeccionComplete ejecutado exitosamente");
      } catch (error) {
        console.error("Error en handleProyeccionComplete:", error);
        setError(obtenerMensajeError(error, "Error al procesar la proyección"));
        setDisableNext(true);
      }
    };

    const saveFinanzas = async () => {
      if (!procesoId) return false;
      setIsSaving(true);
      try {
        // Validar que haya al menos un ingreso y un egreso con datos
        const hasValidIngresos = ingresos.some((i) => i.concepto && i.monto);
        const hasValidEgresos = egresos.some((e) => e.concepto && e.monto);
        if (!hasValidIngresos || !hasValidEgresos) {
          setError("Debe ingresar al menos un ingreso y un gasto");
          return false;
        }
        // Guardar ingresos
        for (const ingreso of ingresos.filter(
          (i) => (!i.saved || i.changed) && i.concepto && i.monto
        )) {
          const data = {
            proceso: procesoId,
            tipo_finanza: "INGRESO",
            tipo_ingreso: ingreso.concepto,
            tipo_gasto: null,
            valor: ingreso.monto.toString(),
          };
          let response;
          if (ingreso.id && ingreso.saved) {
            response = await ProcesosApi.actualizarFinanza(procesoId, ingreso.id, data);
          } else {
            response = await ProcesosApi.registrarFinanza(procesoId, data);
          }
          const savedItem = response.data;
          setIngresos((prevIngresos) =>
            prevIngresos.map((i) =>
              i.id === ingreso.id ? { ...i, id: savedItem.id, saved: true, changed: false } : i
            )
          );
        }

        // Guardar egresos
        for (const egreso of egresos.filter(
          (e) => (!e.saved || e.changed) && e.concepto && e.monto
        )) {
          const data = {
            proceso: procesoId,
            tipo_finanza: "GASTO",
            tipo_ingreso: null,
            tipo_gasto: egreso.concepto,
            valor: egreso.monto.toString(),
          };

          let response;
          if (egreso.id && egreso.saved) {
            response = await ProcesosApi.actualizarFinanza(procesoId, egreso.id, data);
          } else {
            response = await ProcesosApi.registrarFinanza(procesoId, data);
          }

          const savedItem = response.data;
          setEgresos((prevEgresos) =>
            prevEgresos.map((e) =>
              e.id === egreso.id ? { ...e, id: savedItem.id, saved: true, changed: false } : e
            )
          );
        }

        // Actualizar la proceso con el compromiso mensual
        await ProcesosApi.actualizarProceso(procesoId, {
          compromiso_mensual: totals.disponible.toString(),
        });

        setHasChanges(false);
        setError(undefined);
        return true;
      } catch (error) {
        console.error("Error al guardar datos:", error);
        setError(obtenerMensajeError(error, "Error al guardar los datos financieros"));
        return false;
      } finally {
        setIsSaving(false);
      }
    };

    const eliminarIngreso = async (id) => {
      try {
        const item = ingresos.find((i) => i.id === id);
        if (item?.saved) {
          await ProcesosApi.eliminarFinanza(procesoId, id);
        }

        setIngresos((prevIngresos) => {
          const updatedIngresos = prevIngresos.filter((item) => item.id !== id);
          return updatedIngresos.length === 0 ? [createEmptyRow()] : updatedIngresos;
        });
        setGridKey((prev) => prev + 1);
        setHasChanges(true);
      } catch (error) {
        console.error("Error al eliminar finanza:", error);
        setError(obtenerMensajeError(error, "Error al eliminar el ingreso"));
      }
    };

    const eliminarEgreso = async (id) => {
      try {
        const item = egresos.find((e) => e.id === id);
        if (item?.saved) {
          await ProcesosApi.eliminarFinanza(procesoId, id);
        }

        setEgresos((prevEgresos) => {
          const updatedEgresos = prevEgresos.filter((item) => item.id !== id);
          return updatedEgresos.length === 0 ? [createEmptyRow()] : updatedEgresos;
        });
        setGridKey((prev) => prev + 1);
        setHasChanges(true);
      } catch (error) {
        console.error("Error al eliminar finanza:", error);
        setError(obtenerMensajeError(error, "Error al eliminar el gasto"));
      }
    };

    const agregarIngreso = () => {
      setIngresos((prev) => [...prev, createEmptyRow()]);
      setGridKey((prev) => prev + 1);
      setHasChanges(true);
    };

    const agregarEgreso = () => {
      setEgresos((prev) => [...prev, createEmptyRow()]);
      setGridKey((prev) => prev + 1);
      setHasChanges(true);
    };

    const handleIngresoChange = (id, field, value) => {
      setIngresos((prevIngresos) =>
        prevIngresos.map((item) =>
          item.id === id ? { ...item, [field]: value || "", changed: true } : item
        )
      );
      setHasChanges(true);
    };

    const handleEgresoChange = (id, field, value) => {
      setEgresos((prevEgresos) =>
        prevEgresos.map((item) =>
          item.id === id ? { ...item, [field]: value || "", changed: true } : item
        )
      );
      setHasChanges(true);
    };

    const formatTotal = (value) => {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    };

    const handleSaveAll = () => {
      saveFinanzas();
    };

    const createColumns = (handleChange, handleDelete) => [
      {
        field: "concepto",
        headerName: "CONCEPTO",
        flex: 2,
        minWidth: 200,
        renderHeader: () => (
          <SuiTypography variant="caption" fontWeight="medium" color="text">
            CONCEPTO
          </SuiTypography>
        ),
        renderCell: (params) => (
          <SuiInput
            placeholder="Concepto"
            value={params.row.concepto || ""}
            onChange={(e) => handleChange(params.row.id, "concepto", e.target.value)}
            fullWidth
            size="small"
            inputProps={{
              style: { width: "100%" },
              onKeyDown: (e) => {
                if (e.key === " " || e.key === "Backspace") {
                  e.stopPropagation();
                }
              },
              onFocus: (e) => {
                e.stopPropagation();
                // Asegurarse de que el padre no reciba el foco
                e.target.parentElement.setAttribute("tabIndex", "-1");
              },
              onBlur: (e) => {
                e.stopPropagation();
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        ),
      },
      {
        field: "monto",
        headerName: "MONTO",
        flex: 1,
        minWidth: 150,
        renderHeader: () => (
          <SuiTypography variant="caption" fontWeight="medium" color="text">
            MONTO
          </SuiTypography>
        ),
        renderCell: (params) => (
          <MontoInput
            value={params.row.monto}
            onChange={(value) => handleChange(params.row.id, "monto", value)}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            onClick={() => handleDelete(params.row.id)}
            size="small"
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];

    return (
      <Card sx={{ p: 3, m: 2, boxShadow: "none" }}>
        {!hasPropuesta && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Debe generar una proyección de pagos antes de continuar con el siguiente paso
          </Alert>
        )}
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SuiBox display="flex" justifyContent="space-between" alignItems="center">
            <SuiTypography variant="h6" fontWeight="medium" sx={{ mr: 2 }}>
              Disponible para Pago
            </SuiTypography>
            <SuiBadge
              variant="gradient"
              color={totals.disponible >= 0 ? "success" : "error"}
              size="large"
              badgeContent={
                <span style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {formatTotal(totals.disponible)}
                </span>
              }
              container
            />
          </SuiBox>

          <SuiBox display="flex" gap={2}>
            <Tooltip
              title={
                !hasChanges
                  ? "No hay cambios pendientes por guardar"
                  : isSaving
                  ? "Guardando cambios..."
                  : ""
              }
              placement="top"
            >
              <span>
                {" "}
                {/* Necesario para que el Tooltip funcione con botones deshabilitados */}
                <SuiButton
                  variant="gradient"
                  buttonColor={hasChanges ? "info" : "light"}
                  onClick={handleSaveAll}
                  disabled={!hasChanges || isSaving}
                  startIcon={<SaveIcon />}
                  sx={{
                    minWidth: "160px",
                    transition: "all 0.3s",
                  }}
                >
                  {isSaving ? "Guardando..." : "Guardar Finanzas"}
                </SuiButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                hasChanges
                  ? "Guarda los cambios pendientes antes de generar una proyección"
                  : isSaving
                  ? "Espera mientras se guardan los cambios"
                  : ""
              }
              placement="top"
            >
              <span>
                <SuiButton
                  variant="gradient"
                  buttonColor={hasChanges || isSaving ? "light" : "info"}
                  onClick={() => setShowProyeccion(true)}
                  disabled={hasChanges || isSaving}
                  sx={{
                    minWidth: "160px",
                    transition: "all 0.3s",
                  }}
                >
                  Generar Proyección
                </SuiButton>
              </span>
            </Tooltip>
          </SuiBox>
        </SuiBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SuiBox>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SuiTypography variant="subtitle2" fontWeight="medium">
                  Ingresos
                </SuiTypography>
                <SuiBadge
                  className={classes.badge}
                  variant="gradient"
                  color="info"
                  size="large"
                  badgeContent={
                    <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {formatTotal(totals.totalIngresos)}
                    </span>
                  }
                  container
                />
              </SuiBox>

              <GridContainer>
                <DataGrid
                  key={`ingresos-${gridKey}`}
                  rows={ingresos}
                  columns={createColumns(handleIngresoChange, eliminarIngreso)}
                  hideFooter
                  hideFooterPagination
                  hideFooterSelectedRowCount
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableSelectionOnClick
                  headerHeight={HEADER_HEIGHT}
                  rowHeight={ROW_HEIGHT}
                  autoHeight={false}
                  getRowHeight={() => ROW_HEIGHT}
                  columnBuffer={3}
                  sx={{
                    height: TOTAL_HEIGHT,
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-columnHeader:focus": {
                      outline: "none",
                    },
                    // Prevenir el foco en la barra de desplazamiento
                    "& .MuiDataGrid-scrollbar": {
                      '&[aria-hidden="true"]': {
                        "&:focus": {
                          outline: "none",
                        },
                        "&:focus-within": {
                          outline: "none",
                        },
                      },
                    },
                  }}
                  initialState={{
                    sorting: {
                      sortModel: [],
                    },
                  }}
                  getRowId={(row) => row.id}
                  componentsProps={{
                    row: {
                      onFocus: (event) => event.stopPropagation(), // Prevenir la propagación del foco
                    },
                    cell: {
                      onFocus: (event) => event.stopPropagation(), // Prevenir la propagación del foco
                    },
                  }}
                  slotProps={{
                    // Configuración para el manejo del foco en los slots
                    row: {
                      tabIndex: -1, // Prevenir el foco en las filas
                    },
                    cell: {
                      tabIndex: -1, // Prevenir el foco en las celdas que no son inputs
                    },
                  }}
                />
              </GridContainer>

              <SuiBox mt={2}>
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={agregarIngreso}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Agregar Ingreso
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <SuiBox>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SuiTypography variant="subtitle2" fontWeight="medium">
                  Gastos de Subsistencia
                </SuiTypography>
                <SuiBadge
                  variant="gradient"
                  color="error"
                  size="large"
                  badgeContent={
                    <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {formatTotal(totals.totalEgresos)}
                    </span>
                  }
                  container
                />
              </SuiBox>

              <GridContainer>
                <DataGrid
                  key={`egresos-${gridKey}`}
                  rows={egresos}
                  columns={createColumns(handleEgresoChange, eliminarEgreso)}
                  hideFooter
                  hideFooterPagination
                  hideFooterSelectedRowCount
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableSelectionOnClick
                  headerHeight={HEADER_HEIGHT}
                  rowHeight={ROW_HEIGHT}
                  autoHeight={false}
                  getRowHeight={() => ROW_HEIGHT}
                  columnBuffer={3}
                  sx={{
                    height: TOTAL_HEIGHT,
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-columnHeader:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-scrollbar": {
                      '&[aria-hidden="true"]': {
                        "&:focus": {
                          outline: "none",
                        },
                        "&:focus-within": {
                          outline: "none",
                        },
                      },
                    },
                  }}
                  initialState={{
                    sorting: {
                      sortModel: [],
                    },
                  }}
                  getRowId={(row) => row.id}
                  componentsProps={{
                    row: {
                      onFocus: (event) => event.stopPropagation(),
                    },
                    cell: {
                      onFocus: (event) => event.stopPropagation(),
                    },
                  }}
                  slotProps={{
                    row: {
                      tabIndex: -1,
                    },
                    cell: {
                      tabIndex: -1,
                    },
                  }}
                />
              </GridContainer>

              <SuiBox mt={2}>
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={agregarEgreso}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Agregar Gasto
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </Grid>
        </Grid>
        <DescripcionGeneral
          procesoId={procesoId}
          propuestaId={propuestaIdRef.current}
          setError={setError}
          tipo_propuesta="solicitud"
        />

        <ProyeccionModal
          open={showProyeccion}
          onClose={() => setShowProyeccion(false)}
          procesoId={procesoId}
          disponible={totals.disponible}
          tipo_propuesta="solicitud"
          onComplete={handleProyeccionComplete}
          onError={(error) => {
            // Los errores de validación del modal se muestran dentro del modal
            // Solo propagamos errores críticos al stepper
            console.error("Error en proyección:", error);
          }}
        />
      </Card>
    );
  }
);

FinanzasForm.propTypes = {
  procesoId: PropTypes.number.isRequired,
  setFormData: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setDisableNext: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
};

export default FinanzasForm;
